/**
 * propertyMediaModel — PURE model + query-spec + display helpers for the
 * client reference-media collection (`properties/{pid}/media`).
 *
 * Pure / impure split (same idiom as propertyModel.ts ↔ propertyStore.ts):
 * everything here is firebase-free so it is unit-testable under the Node test
 * runner. The firebase I/O (`listenPropertyMedia`) lives in `propertyMedia.ts`,
 * which builds its real Firestore constraints from `buildMediaQuerySpec` here.
 *
 * Field names + types are FROZEN by Silas's read contract
 * (scratchpad/media-read-contract.md §1–3). READ-ONLY — no write shapes here.
 */

/** What a media item IS. */
export type MediaKind = 'photo' | 'video' | 'map' | 'plan';

/** How it arrived. Ingestion currently emits the first four; 'email'/'staff'
 *  are reserved — render the tag tolerantly, never assume only four. */
export type MediaSource =
  | 'whatsapp' | 'drive' | 'upload' | 'intake' | 'email' | 'staff';

/** Soft-delete state. The pane only ever receives 'active' (the query filters);
 *  the field is on the type so callers don't widen it ad-hoc. */
export type MediaStatus = 'active' | 'archived' | 'trashed';

/**
 * One client-media item as the reference pane consumes it. Read shape of a
 * `properties/{propertyId}/media/{mediaId}` document. Optional fields are
 * genuinely optional in the store — render defensively (no media = empty pane).
 */
export interface PropertyMedia {
  /** Firestore doc id — `m_<epoch>_<rand>`. Stable React key. */
  mediaId: string;
  /** photo | video | map | plan — drives which tab + which viewer. */
  kind: MediaKind;
  /** Storage download URL — the <img>/<video>/<a> src. */
  url: string;
  /** Thumbnail URL (esp. video poster + grid cells). Falls back to `url`. */
  thumbUrl?: string;
  /** Free-text caption shown under the preview. */
  label?: string;
  /** Floor tag (carried from intake `MediaInput.floor`) — secondary filter. */
  floor?: string;
  /** Room/place tag (carried from intake `MediaInput.place`) — secondary filter. */
  place?: string;
  /** Which feeder delivered it — render as a small provenance chip. */
  source: MediaSource;
  /** The VP### this item was captured for. Absent = property-intrinsic. */
  projectId?: string;
  /** Whose media — the privacy wall. Always present; never render cross-client. */
  clientId: string;
  /** When the photo/video was actually TAKEN (display only; may be absent —
   *  do NOT rely on it for the query sort). */
  capturedAt?: string;
  /** When it landed in our store (server time, ISO). Always present — the
   *  durable sort key. */
  uploadedAt: string;
  /** Uploader: a uid, the literal 'client', or a contactId. */
  uploadedBy: string;
  /** mime type — drives icon/viewer choice (image/* vs video/* vs application/pdf). */
  mimeType?: string;
}

// ─── Query scope + spec ─────────────────────────────────────────────────────

export type MediaScope = 'engagement' | 'building';

export interface MediaQuery {
  propertyId: string;
  clientId: string;
  /** required when scope === 'engagement'; ignored when 'building'. */
  projectId?: string;
  /** 'engagement' (default) narrows to this VP###; 'building' = whole library. */
  scope: MediaScope;
}

/** A single equality clause descriptor — mapped to Firestore `where(...)` in
 *  propertyMedia.ts. Kept pure so the query shape is unit-testable. */
export interface MediaWhereClause {
  field: 'clientId' | 'status' | 'projectId';
  op: '==';
  value: string;
}

export interface MediaQuerySpec {
  where: MediaWhereClause[];
  /** FROZEN: sort by `uploadedAt` (always present), NEVER `capturedAt`
   *  (optional → Firestore would silently drop docs missing it). Contract F1. */
  orderBy: { field: 'uploadedAt'; dir: 'desc' };
}

/**
 * Build the FROZEN query spec for a media listen. The `clientId` privacy wall
 * + `status == 'active'` are present in BOTH scopes; `projectId` is added only
 * for `engagement` (and only when a projectId is supplied). A different
 * client's media is never returned in either scope.
 */
export function buildMediaQuerySpec(q: MediaQuery): MediaQuerySpec {
  const where: MediaWhereClause[] = [
    { field: 'clientId', op: '==', value: q.clientId }, // privacy wall — ALWAYS
    { field: 'status', op: '==', value: 'active' },
  ];
  if (q.scope === 'engagement' && q.projectId) {
    where.push({ field: 'projectId', op: '==', value: q.projectId });
  }
  return { where, orderBy: { field: 'uploadedAt', dir: 'desc' } };
}

// ─── Display helpers (pure) ─────────────────────────────────────────────────

/** The three viewer tabs. `map` AND `plan` both surface under "Maps". */
export type MediaTab = 'photos' | 'videos' | 'maps';

export const MEDIA_TABS: readonly MediaTab[] = ['photos', 'videos', 'maps'] as const;

export function tabForKind(kind: MediaKind): MediaTab {
  if (kind === 'video') return 'videos';
  if (kind === 'map' || kind === 'plan') return 'maps';
  return 'photos';
}

/** Partition items into the three tabs (each list keeps the input order). */
export function splitByTab(
  items: PropertyMedia[],
): Record<MediaTab, PropertyMedia[]> {
  const out: Record<MediaTab, PropertyMedia[]> = {
    photos: [],
    videos: [],
    maps: [],
  };
  for (const m of items) out[tabForKind(m.kind)].push(m);
  return out;
}

/** The instant the pane treats as an item's "time" — captured if known, else
 *  uploaded. Display + client-side sort only. */
export function displayTime(m: PropertyMedia): string {
  return m.capturedAt ?? m.uploadedAt;
}

/**
 * Sort for display by `capturedAt ?? uploadedAt`, newest first. The query
 * already orders by `uploadedAt` desc; this re-sort honours EXIF capture time
 * when present without ever dropping items that lack it (contract F1).
 */
export function sortMediaForDisplay(items: PropertyMedia[]): PropertyMedia[] {
  return [...items].sort((a, b) =>
    displayTime(a) < displayTime(b) ? 1 : displayTime(a) > displayTime(b) ? -1 : 0,
  );
}

/** Distinct, defined floor tags across the items (in first-seen order). */
export function distinctFloors(items: PropertyMedia[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of items) {
    if (m.floor && !seen.has(m.floor)) {
      seen.add(m.floor);
      out.push(m.floor);
    }
  }
  return out;
}

/**
 * Optional client-side secondary filter (contract §3 / F5 — kept off the query
 * index). Empty/absent floor or place is a pass-through.
 */
export function filterMediaByTags(
  items: PropertyMedia[],
  opts: { floor?: string; place?: string } = {},
): PropertyMedia[] {
  const { floor, place } = opts;
  if (!floor && !place) return items;
  return items.filter(
    (m) => (!floor || m.floor === floor) && (!place || m.place === place),
  );
}

// ─── Provenance labels (pure) ───────────────────────────────────────────────

const SOURCE_LABELS: Record<MediaSource, string> = {
  whatsapp: 'WhatsApp',
  drive: 'Drive',
  upload: 'Upload link',
  intake: 'Intake form',
  email: 'Email',
  staff: 'Staff',
};

/** Human label for a provenance chip. Tolerant of unknown sources. */
export function sourceLabel(source: MediaSource | string): string {
  return SOURCE_LABELS[source as MediaSource] ?? String(source);
}

/** Is this a video item (drives <video> vs <img> in the viewer)? */
export function isVideo(m: PropertyMedia): boolean {
  return m.kind === 'video' || (m.mimeType ?? '').startsWith('video/');
}
